import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import ContentMarkup from '@/components/content-markup';
import { cn } from '@/lib/utils';

/**
 * Styles applied to the legal links content-asset markup — `<ul><li><a>` links wrapped in
 * a single `<div>`, see the `footer-link-legal` content asset in Business Manager.
 * Links are laid out inline with a "|" separator between each entry.
 */
const FOOTER_LEGAL_LINKS_CONTENT_STYLES = [
    'text-xs text-footer-muted-foreground',
    '[&>div]:flex [&>div]:flex-wrap [&>div]:items-center [&>div]:gap-x-1 [&>div]:gap-y-1',
    '[&_li]:inline-flex [&_li]:list-none [&_li]:items-center',
    "[&_li:not(:last-child)]:after:content-['|'] [&_li:not(:last-child)]:after:mx-2 [&_li:not(:last-child)]:after:text-footer-muted-foreground/60",
    '[&_a]:transition-colors [&_a]:hover:text-footer-foreground',
].join(' ');

export interface FooterLegalSectionProps {
    /** Legal links markup loaded from the `footer-link-legal` content asset. */
    legal?: string;
    className?: string;
}

/**
 * Renders the footer's bottom row: the merchant-managed legal links (content asset
 * `footer-link-legal`) and the copyright notice. Renders only the copyright when the
 * legal content asset hasn't been configured yet.
 */
export default function FooterLegalSection({ legal, className }: FooterLegalSectionProps): ReactElement {
    const { t } = useTranslation('footer');

    return (
        <div
            className={cn(
                'flex flex-col items-start gap-2 pt-2 sm:flex-row',
                legal ? 'sm:justify-between' : 'sm:justify-end',
                className
            )}>
            {legal && <ContentMarkup content={legal} className={FOOTER_LEGAL_LINKS_CONTENT_STYLES} />}
            <div className="shrink-0 text-xs text-footer-muted-foreground sm:text-right">
                © {new Date().getFullYear()} {t('copyright')}
            </div>
        </div>
    );
}
